import { useCallback, useEffect } from 'react'
import { usePageControl } from './page-control.hook.js'

/** Keys a page-turner (BT pedal or keyboard) emits to go forward / backward. */
export const PAGE_TURN_FORWARD_KEYS = ['PageDown', 'ArrowRight', 'ArrowDown'] as const
export const PAGE_TURN_BACKWARD_KEYS = ['PageUp', 'ArrowLeft', 'ArrowUp'] as const

export const isPageTurnKey = (key: string): boolean =>
  (PAGE_TURN_FORWARD_KEYS as readonly string[]).includes(key) ||
  (PAGE_TURN_BACKWARD_KEYS as readonly string[]).includes(key)

export interface PageTurner {
  /** The view container */
  container?: HTMLElement | null
  /** Function to call when the user request the previous entity */
  onPrevious?(): void
  /** Function to call when the user request the next entity */
  onNext(): void
}

/** Page turner hook */
export const usePageTurner = ({ container, onPrevious = () => {}, onNext = () => {} }: PageTurner) => {
  const c = container ?? document.body

  const { isAtBeginning, isAtEnd, pageUp, pageDown } = usePageControl(c)

  const handlePrev = useCallback(() => {
    if (isAtBeginning) {
      onPrevious()
    } else {
      pageUp()
    }
  }, [isAtBeginning, onPrevious, pageUp])

  const handleNext = useCallback(() => {
    if (isAtEnd) {
      onNext()
    } else {
      pageDown()
    }
  }, [isAtEnd, onNext, pageDown])

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      /**
       * Ignore auto-repeat keydowns. A page-turner pedal (or a physically held
       * key) emits a stream of `repeat: true` keydowns; page-turning and
       * song-advancing are discrete actions that must fire once per physical
       * press. Without this guard a single held press fired `onNext` /
       * `pageDown` dozens of times per second, and on a slower tablet that
       * rapid-fire saturated the main thread mid song-switch and hung the tab
       * (GH #1628). The on-screen controls don't auto-repeat, which is why the
       * hang only reproduced via the page turner.
       */
      if (e.repeat) {
        return
      }
      /** If the target is an editable, don't handle the key */
      if (e.target instanceof HTMLElement && e.target.isContentEditable) {
        return
      }
      if ((PAGE_TURN_BACKWARD_KEYS as readonly string[]).includes(e.key)) {
        handlePrev()
      } else if ((PAGE_TURN_FORWARD_KEYS as readonly string[]).includes(e.key)) {
        handleNext()
      }
    },
    [handleNext, handlePrev]
  )

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onKeyDown, c])

  return { onKeyDown }
}
